import React from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { PrimaryButton, SecondaryButton } from "../../../../layout/Button/Button";
import { isAutoContinueDecisionPoint } from "common/build/utils/branchedStoryUtils";
import { useBranchedStoryEdit } from "../../contexts/BranchedStoryEditContext";
import { VCard } from "../../../../../core/components/VCard";

const useStyles = makeStyles(() =>
    createStyles({
        formContainer: {
            padding: "24px",
        },
        decisionPoint: {
            backgroundColor: "#EBF7F3",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
            border: "1px solid #1FAF8C",
        },
        branchContainer: {
            marginLeft: "24px",
            paddingLeft: "16px",
            borderLeft: "2px solid #1FAF8C",
            marginBottom: "16px",
        },
        branchOption: {
            backgroundColor: "#ffffff",
            borderRadius: "4px",
            padding: "12px",
            marginBottom: "8px",
            border: "1px solid #e0e0e0",
        },
        actionButtons: {
            marginTop: "32px",
            display: "flex",
            gap: "16px",
            justifyContent: "flex-end",
        },
        previewCard: {
            marginBottom: "16px",
        },
    }),
);

interface EditDecisionPointsProps {
    onComplete: () => void;
}

interface DecisionPoint {
    id: number;
    decisionText: string;
    optionA: string;
    optionB: string;
    targetPartA: number;
    targetPartB: number;
    sourcePartNum: number;
}

export const EditDecisionPoints: React.FC<EditDecisionPointsProps> = ({ onComplete }) => {
    const classes = useStyles();
    const { currentStory, updateDecisionPoint, editedDecisionPoints } = useBranchedStoryEdit();

    // Get decision points from story parts and format them for editing
    const getDecisionPointsFromStory = () => {
        const decisionPoints: DecisionPoint[] = [];

        if (currentStory.StoryParts) {
            currentStory.StoryParts.forEach((part) => {
                if (part.StoryDecisionPoints && part.StoryDecisionPoints.length > 0) {
                    part.StoryDecisionPoints.forEach((dp) => {
                        // Filter out auto-continues
                        if (!isAutoContinueDecisionPoint(dp)) {
                            decisionPoints.push({
                                id: dp.id,
                                decisionText: dp.decisionText || "",
                                optionA: (dp.branches && dp.branches[0]?.choiceText) || "",
                                optionB: (dp.branches && dp.branches[1]?.choiceText) || "",
                                targetPartA: (dp.branches && dp.branches[0]?.targetPartNumber) || 0,
                                targetPartB: (dp.branches && dp.branches[1]?.targetPartNumber) || 0,
                                sourcePartNum: part.part,
                            });
                        }
                    });
                }
            });
        }

        return decisionPoints;
    };

    const displayDecisionPoints = getDecisionPointsFromStory();

    // Get current value for a decision point (from edited state if available, otherwise from original)
    const getCurrentValue = (pointId: number, field: keyof DecisionPoint) => {
        const editedPoint = editedDecisionPoints.get(pointId);
        const originalPoint = displayDecisionPoints.find((dp) => dp.id === pointId);

        if (editedPoint && field in editedPoint) {
            return (editedPoint as unknown as DecisionPoint)[field];
        }

        return originalPoint ? originalPoint[field] : "";
    };

    const handleDecisionChange = (pointId: number, field: keyof DecisionPoint, value: string) => {
        const currentDP = displayDecisionPoints.find((dp) => dp.id === pointId);
        if (!currentDP) return;

        const part = currentStory.StoryParts?.find((p) => p.part === currentDP.sourcePartNum);

        if (part) {
            // Build update with current values + changed field
            const update: Record<string, unknown> = {
                id: pointId,
                storyPartId: part.id,
                decisionText: currentDP?.decisionText || "",
                order: 1,
                [field]: value,
            };

            // For new DPs, include branches so they get created
            const existingDP = part.StoryDecisionPoints?.[0];
            if (!existingDP) {
                update.branches = [
                    {
                        branchLabel: "A",
                        choiceText: field === "optionA" ? value : currentDP?.optionA || "",
                        targetPartNumber: currentDP?.targetPartA || 0,
                        order: 1,
                    },
                    {
                        branchLabel: "B",
                        choiceText: field === "optionB" ? value : currentDP?.optionB || "",
                        targetPartNumber: currentDP?.targetPartB || 0,
                        order: 2,
                    },
                ];
            }

            updateDecisionPoint(pointId, update);
        }
    };

    const handleBranchTargetChange = (pointId: number, branchIndex: number, targetPartNumber: number) => {
        const dpIndex = displayDecisionPoints.findIndex((dp) => dp.id === pointId);
        const currentDP = displayDecisionPoints[dpIndex];
        if (!currentDP) return;

        const partNum = currentDP.sourcePartNum;
        const part = currentStory.StoryParts?.find((p) => p.part === partNum);
        const dp = part?.StoryDecisionPoints?.find((d) => d.id === pointId);

        if (!part) return;

        if (dp && dp.branches) {
            // Existing DP — update branch target
            const updatedBranches = dp.branches.map((b, i) => ({
                id: b.id,
                branchLabel: b.branchLabel,
                choiceText: b.choiceText,
                targetPartNumber: i === branchIndex ? targetPartNumber : b.targetPartNumber,
                order: b.order,
            }));

            updateDecisionPoint(pointId, {
                id: pointId,
                storyPartId: part.id,
                decisionText: dp.decisionText,
                order: dp.order,
                branches: updatedBranches,
            });
        } else {
            // New DP (placeholder) — create with branches
            const branches = [
                {
                    branchLabel: "A",
                    choiceText: currentDP?.optionA || "Option A",
                    targetPartNumber: branchIndex === 0 ? targetPartNumber : currentDP?.targetPartA || 0,
                    order: 1,
                },
                {
                    branchLabel: "B",
                    choiceText: currentDP?.optionB || "Option B",
                    targetPartNumber: branchIndex === 1 ? targetPartNumber : currentDP?.targetPartB || 0,
                    order: 2,
                },
            ];

            updateDecisionPoint(pointId, {
                id: pointId,
                storyPartId: part.id,
                decisionText: currentDP?.decisionText || "",
                order: 1,
                branches,
            });
        }
    };

    return (
        <VCard>
            <Box className={classes.formContainer}>
                <Typography variant="h4" gutterBottom>
                    Edit Decision Points
                </Typography>

                {/* Display editable decision points */}
                {displayDecisionPoints && displayDecisionPoints.length > 0 && (
                    <Box mt={3}>
                        <Typography variant="h6" gutterBottom>
                            Decision Points ({displayDecisionPoints.length} decision points)
                        </Typography>
                        <Typography variant="body1" paragraph style={{ color: "#666" }}>
                            Configure the decision points in your story. Each decision point should include the decision
                            question and the possible path options.
                        </Typography>

                        {displayDecisionPoints.map((decisionPoint, index) => (
                            <Card key={decisionPoint.id} className={classes.previewCard} elevation={1}>
                                <CardContent>
                                    <Box mb={2}>
                                        <Chip
                                            label={`After Story Part ${decisionPoint.sourcePartNum}`}
                                            size="small"
                                            color="primary"
                                            style={{ marginBottom: "16px" }}
                                        />
                                        <Typography variant="h6" gutterBottom>
                                            Decision Point {index + 1}
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label={`Decision Point ${index + 1} Question`}
                                                placeholder="e.g., Does Phillip send photo (option A) or decline to send photo (option B)?"
                                                value={getCurrentValue(decisionPoint.id, "decisionText")}
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleDecisionChange(
                                                        decisionPoint.id,
                                                        "decisionText",
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Option A"
                                                placeholder="e.g., Phillip sends photo"
                                                value={getCurrentValue(decisionPoint.id, "optionA")}
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleDecisionChange(decisionPoint.id, "optionA", e.target.value)
                                                }
                                            />
                                            <FormControl size="small" variant="outlined" fullWidth sx={{ mt: 1 }}>
                                                <InputLabel>Target Part (A)</InputLabel>
                                                <Select
                                                    value={getCurrentValue(decisionPoint.id, "targetPartA")}
                                                    label="Target Part (A)"
                                                    onChange={(e) =>
                                                        handleBranchTargetChange(
                                                            decisionPoint.id,
                                                            0,
                                                            Number(e.target.value),
                                                        )
                                                    }
                                                >
                                                    {currentStory.StoryParts?.map((p) => (
                                                        <MenuItem key={p.id} value={p.part}>
                                                            Part {p.part}
                                                            {p.partLabel ? ` - ${p.partLabel}` : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Option B"
                                                placeholder="e.g., Phillip declines to send photo"
                                                value={getCurrentValue(decisionPoint.id, "optionB")}
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleDecisionChange(decisionPoint.id, "optionB", e.target.value)
                                                }
                                            />
                                            <FormControl size="small" variant="outlined" fullWidth sx={{ mt: 1 }}>
                                                <InputLabel>Target Part (B)</InputLabel>
                                                <Select
                                                    value={getCurrentValue(decisionPoint.id, "targetPartB")}
                                                    label="Target Part (B)"
                                                    onChange={(e) =>
                                                        handleBranchTargetChange(
                                                            decisionPoint.id,
                                                            1,
                                                            Number(e.target.value),
                                                        )
                                                    }
                                                >
                                                    {currentStory.StoryParts?.map((p) => (
                                                        <MenuItem key={p.id} value={p.part}>
                                                            Part {p.part}
                                                            {p.partLabel ? ` - ${p.partLabel}` : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}

                {/* Action Buttons */}
                <Box className={classes.actionButtons}>
                    <SecondaryButton type="button">Skip for Now</SecondaryButton>
                    <PrimaryButton onClick={onComplete}>Continue to Questions</PrimaryButton>
                </Box>
            </Box>
        </VCard>
    );
};

// Display name for debugging (following design guide)
EditDecisionPoints.displayName = "EditDecisionPoints";
